import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Plus,
  Minus,
  Link as LinkIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, Timestamp, increment, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getProfilePlaceholder } from '../../utils/placeholders';
import campaignService from '../../services/campaignService';
import notificationService from '../../services/notificationService';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml } from '../../security/sanitize';
import VerificationResultsDisplay from '../../components/brand/VerificationResultsDisplay';
import Breadcrumb from '../../components/common/Breadcrumb';

const ContentApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('pending');
  const [reviewType, setReviewType] = useState('proof'); // 'draft' or 'proof'
  
  // Feedback notification state
  const { showSuccess, showError, handleAsyncOperation } = useFeedback();



  useEffect(() => {
    const fetchReviewData = async () => {
      if (!currentUser || !id) {
        setLoading(false);
        return;
      }

      try {
        let reviewData = null;
        let currentReviewType = 'proof';
        
        // Try to fetch from contentDrafts collection first (pre-approval workflow)
        let draftRef = doc(db, 'contentDrafts', id);
        let draftSnapshot = await getDoc(draftRef);
        
        if (draftSnapshot.exists()) {
          reviewData = draftSnapshot.data();
          currentReviewType = 'draft';
          setReviewType('draft');
        } else {
          // Try to fetch proof document (post-approval workflow)
          let proofRef = doc(db, 'proofs', id);
          let proofSnapshot = await getDoc(proofRef);
          
          // If not found, try reviews collection (backward compatibility)
          if (!proofSnapshot.exists()) {
            proofRef = doc(db, 'reviews', id);
            proofSnapshot = await getDoc(proofRef);
          }
          
          if (proofSnapshot.exists()) {
            reviewData = proofSnapshot.data();
            currentReviewType = 'proof';
            setReviewType('proof');
          }
        }
        
        if (!reviewData) {
          setLoading(false);
          return;
        }
        
        setSubmission(reviewData);
        setApprovalStatus(reviewData.status || 'pending');
        setFeedback(reviewData.brandFeedback || '');
        
        // Fetch related campaign data
        if (reviewData.campaignId) {
          const campaignRef = doc(db, 'campaigns', reviewData.campaignId);
          const campaignSnapshot = await getDoc(campaignRef);
          
          if (campaignSnapshot.exists()) {
            setCampaign(campaignSnapshot.data());
          }
        }
        
        // Fetch influencer data
        if (reviewData.influencerId) {
          const influencerRef = doc(db, 'users', reviewData.influencerId);
          const influencerSnapshot = await getDoc(influencerRef);
          
          if (influencerSnapshot.exists()) {
            setInfluencer(influencerSnapshot.data());
          }
        }
        

      } catch (error) {
        console.error('Error fetching proof/review:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewData();
  }, [currentUser, id]);
  
  if (loading) {
    return null;
  }

  if (!submission) {
    return (
      <div className="pb-12 relative">

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Review Not Found</h3>
          <p className="text-gray-600 mb-4">The review submission you're looking for doesn't exist or has been removed.</p>
          <Breadcrumb
            items={[
              { label: 'Content Approval', to: '/brand/content-approval' },
              { label: 'Content Approval Detail' },
            ]}
          />
        </div>
      </div>
    );
  }
  
  // Functions to handle approval and revision requests
  const handleApproveSubmission = async (e) => {
    if (e) e.preventDefault();
    if (processing) return;
    
    // Validate CSRF token
    const csrfToken = document.querySelector('input[name="csrfToken"]')?.value;
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      // Show error feedback
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Sanitize feedback
    const sanitizedFeedback = sanitizeText(feedback || 'Approved');
    
    setProcessing(true);
    try {
      // Determine collection based on review type
      let collectionName;
      if (reviewType === 'draft') {
        collectionName = 'contentDrafts';
      } else {
        // For proof submissions, check if it's in proofs or reviews collection
        collectionName = submission?.postUrl ? 'proofs' : 'reviews';
      }
      
      // 1. Update draft/proof/review status
      const submissionRef = doc(db, collectionName, id);
      await updateDoc(submissionRef, {
        status: 'approved',
        brandFeedback: sanitizedFeedback,
        updatedAt: Timestamp.now()
      });
      
      // 2. Update campaign participant status to show approved
      if (submission.campaignId && submission.influencerId) {
        try {
          const participantsRef = collection(db, 'campaignParticipants');
          const q = query(
            participantsRef,
            where('campaignId', '==', submission.campaignId),
            where('influencerId', '==', submission.influencerId)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const participantId = querySnapshot.docs[0].id;
            const participantRef = doc(db, 'campaignParticipants', participantId);
            await updateDoc(participantRef, {
              submissionStatus: 'approved',
              approvedAt: Timestamp.now()
            });
          }
        } catch (participantError) {
          console.error('Error updating participant status:', participantError);
        }
      }
      
      // 3. Update campaign metrics
      if (submission.campaignId) {
        const campaignRef = doc(db, 'campaigns', submission.campaignId);
        await updateDoc(campaignRef, {
          'metrics.approvedReviews': increment(1),
          updatedAt: Timestamp.now()
        });
      }
      
      // 4. Update influencer metrics
      if (submission.influencerId) {
        const influencerRef = doc(db, 'users', submission.influencerId);
        await updateDoc(influencerRef, {
          'metrics.approvedReviews': increment(1),
          updatedAt: Timestamp.now()
        });
      }
      
      // 5. Update local state
      setApprovalStatus('approved');
      
      // 5. Send notification to influencer
      try {
        if (reviewType === 'draft') {
          // Send draft approval notification
          await notificationService.sendDraftApprovalNotification(
            submission.influencerId,
            submission.campaignId,
            campaign?.title || campaign?.name || 'the campaign',
            id
          );
        } else {
          // Send content approval notification for proof submissions
          await notificationService.createContentApprovalNotification(
            submission.influencerId,
            submission.campaignId,
            campaign?.title || campaign?.name || 'the campaign',
            id,
            'approved'
          );
        }
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }
      
      // Show success feedback
      const successMessage = reviewType === 'draft' 
        ? 'Draft approved successfully!' 
        : 'Review approved successfully!';
      const successDescription = reviewType === 'draft'
        ? 'The content draft has been approved and the influencer can now post their content.'
        : 'The content has been approved and the influencer has been notified.';
      showSuccess(successMessage, successDescription, 4000);
      
    } catch (error) {
      console.error('Error approving submission:', error);
      
      // Show error feedback
      showError('Failed to approve review.', 'Please check your connection and try again.', 6000);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRequestRevision = async (e) => {
    if (e) e.preventDefault();
    if (processing) return;
    
    // Validate CSRF token
    const csrfToken = document.querySelector('input[name="csrfToken"]')?.value;
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      // Show error feedback
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }
    
    // Require feedback for revision request
    if (!feedback.trim()) {
      // Show error message using toast
      showError('Feedback required.', 'Please provide feedback for the revision request.', 5000);
      return;
    }
    
    // Sanitize feedback
    const sanitizedFeedback = sanitizeText(feedback);
    
    setProcessing(true);
    try {
      // Determine collection based on review type
      let collectionName;
      if (reviewType === 'draft') {
        collectionName = 'contentDrafts';
      } else {
        // For proof submissions, check if it's in proofs or reviews collection
        collectionName = submission?.postUrl ? 'proofs' : 'reviews';
      }
      
      // 1. Update draft/proof/review status
      const submissionRef = doc(db, collectionName, id);
      await updateDoc(submissionRef, {
        status: 'rejected',
        brandFeedback: sanitizedFeedback,
        updatedAt: Timestamp.now()
      });
      
      // 2. Update campaign participant status to show revision needed
      if (submission.campaignId && submission.influencerId) {
        try {
          const participantsRef = collection(db, 'campaignParticipants');
          const q = query(
            participantsRef,
            where('campaignId', '==', submission.campaignId),
            where('influencerId', '==', submission.influencerId)
          );
          
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const participantId = querySnapshot.docs[0].id;
            const participantRef = doc(db, 'campaignParticipants', participantId);
            await updateDoc(participantRef, {
              submissionStatus: 'revision_requested',
              revisionFeedback: sanitizedFeedback,
              revisionRequestedAt: Timestamp.now()
            });
          }
        } catch (participantError) {
          console.error('Error updating participant status:', participantError);
        }
      }
      
      // 3. Update campaign metrics
      if (submission.campaignId) {
        const campaignRef = doc(db, 'campaigns', submission.campaignId);
        await updateDoc(campaignRef, {
          'metrics.rejectedReviews': increment(1),
          updatedAt: Timestamp.now()
        });
      }
      
      // 4. Update local state
      setApprovalStatus('rejected');
      
      // 4. Send notification to influencer
      try {
        if (reviewType === 'draft') {
          // Send draft rejection notification
          await notificationService.sendDraftRejectionNotification(
            submission.influencerId,
            submission.campaignId,
            campaign?.title || campaign?.name || 'the campaign',
            id,
            sanitizedFeedback
          );
        } else {
          // Send content rejection notification for proof submissions
          await notificationService.createContentApprovalNotification(
            submission.influencerId,
            submission.campaignId,
            campaign?.title || campaign?.name || 'the campaign',
            id,
            'rejected'
          );
        }

      } catch (notifError) {
        console.error('Error sending notification:', notifError);
      }
      
      // Show success feedback
      const successMessage = reviewType === 'draft' 
        ? 'Draft rejected and feedback sent to influencer' 
        : 'Revision requested and feedback sent to influencer';
      const successDescription = reviewType === 'draft'
        ? 'Your feedback has been sent and the influencer can now revise and resubmit their draft.'
        : 'Your feedback has been sent and the influencer can now resubmit their content.';
      showSuccess(successMessage, successDescription, 4000);
      
    } catch (error) {
      console.error('Error requesting revision:', error);
      setProcessing(false);
      // Show error message using toast
      showError('Failed to request revision.', 'Please check your connection and try again.', 6000);
    } finally {
      setProcessing(false);
    }
  };
  
  // Render star rating component
  const renderStarRating = (rating) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-5 w-5 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
        <span className="ml-2 text-gray-700">{rating}/5</span>
      </div>
    );
  };

  return (
    <div>
      {/* Toast Notifications */}

      {/* Header with back button */}
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Content Approval', to: '/brand/content-approval' },
            { label: 'Content Approval Detail' },
          ]}
        />
        <h2 className="text-2xl font-semibold text-gray-800 mt-4">
          {reviewType === 'draft' ? 'Content Draft Review' : submission?.postUrl ? 'Proof Submission Details' : 'Review Details'}
        </h2>
      </div>
      
      {/* Review Submission Details */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="p-6">
          {/* Submission Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div className="flex items-center mb-4 md:mb-0">
              <img
                src={influencer?.profileImageUrl || submission?.influencerProfileImage || getProfilePlaceholder(influencer?.displayName || submission?.influencerName || 'User')}
                alt={influencer?.displayName || submission?.influencerName || 'Influencer'}
                className="h-10 w-10 rounded-full mr-3 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.onerror = null;
                  e.target.src = getProfilePlaceholder(influencer?.displayName || submission?.influencerName || 'User');
                }}
              />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {influencer?.displayName || submission?.influencerName || 'Influencer'}{' '}
                  <span className="text-sm font-normal text-gray-500">
                    {submission?.influencerEmail ? `(${submission.influencerEmail})` : ''}
                  </span>
                </h3>
                <p className="text-sm text-gray-500">
                  Campaign: {campaign?.title || submission?.campaignTitle || 'Campaign'}
                </p>
              </div>
            </div>
            <div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${approvalStatus === 'approved' ? 'bg-green-100 text-green-800' : approvalStatus === 'rejected' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {approvalStatus === 'approved' ? (
                  <>
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Approved
                  </>
                ) : approvalStatus === 'rejected' ? (
                  <>
                    <XCircle className="mr-1 h-3 w-3" />
                    Need Revision
                  </>
                ) : (
                  <>
                    <Clock className="mr-1 h-3 w-3" />
                    Pending Review
                  </>
                )}
              </span>
            </div>
          </div>
          
          {/* Conditional Content Display */}
          {reviewType === 'draft' ? (
            /* DRAFT CONTENT DISPLAY */
            <>
              {/* Draft Media Preview */}
              {submission?.mediaFiles && submission.mediaFiles.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Draft Media</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {submission.mediaFiles.map((media, index) => (
                      <div key={index} className="relative group">
                        {media.type === 'image' ? (
                          <img
                            src={media.url}
                            alt={`Draft media ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg shadow-sm"
                          />
                        ) : (
                          <video
                            src={media.url}
                            className="w-full h-32 object-cover rounded-lg shadow-sm"
                            controls
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Planned Caption */}
              {submission?.plannedCaption && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Planned Caption</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{submission.plannedCaption}</p>
                  </div>
                </div>
              )}

              {/* Planned Hashtags */}
              {submission?.plannedHashtags && submission.plannedHashtags.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Planned Hashtags</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {submission.plannedHashtags.map((hashtag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          #{hashtag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Planned Mentions */}
              {submission?.plannedMentions && submission.plannedMentions.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Planned Mentions</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {submission.plannedMentions.map((mention, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          @{mention}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Platform */}
              {submission?.platform && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Target Platform</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 capitalize">
                      {submission.platform}
                    </span>
                  </div>
                </div>
              )}
            </>
          ) : submission?.postUrl ? (
            /* PROOF SUBMISSION DISPLAY */
            <>
              {/* Automated Verification Results */}
              {submission?.verificationResults && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Automated Verification Results</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    {/* Required Checks */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Required Checks:</p>
                      <div className="space-y-2">
                        {Object.entries(submission.verificationResults.required || {}).map(([key, check]) => (
                          <div key={key} className="flex items-start">
                            {check.passed ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className={`text-sm font-medium ${check.passed ? 'text-green-700' : 'text-red-700'}`}>
                                {check.message}
                              </p>
                              {check.details && (
                                <p className="text-xs text-gray-600 mt-0.5">{check.details}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Advisory Checks */}
                    {submission.verificationResults.advisory && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Advisory Checks:</p>
                        <div className="space-y-2">
                          {Object.entries(submission.verificationResults.advisory).map(([key, check]) => (
                            <div key={key} className="flex items-start">
                              {check.passed && !check.warning ? (
                                <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              ) : (
                                <Clock className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                              )}
                              <div>
                                <p className={`text-sm ${check.passed && !check.warning ? 'text-green-700' : 'text-yellow-700'}`}>
                                  {check.message}
                                </p>
                                {check.details && (
                                  <p className="text-xs text-gray-600 mt-0.5">{check.details}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Post URL */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Post URL</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <a 
                    href={submission.postUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline break-all flex items-center"
                  >
                    <LinkIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                    {submission.postUrl}
                  </a>
                </div>
              </div>
              
              {/* Screenshots */}
              {submission?.screenshotUrls && submission.screenshotUrls.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Screenshots ({submission.screenshotUrls.length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {submission.screenshotUrls.map((url, index) => (
                      <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img 
                          src={url} 
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Content File (Video or Photo) */}
              {submission?.contentUrl && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Content {submission.contentType === 'video' ? 'Video' : 'Photo'}
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {submission.contentType === 'video' ? (
                      <video 
                        controls 
                        className="w-full max-h-96 rounded-lg"
                        src={submission.contentUrl}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <a href={submission.contentUrl} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={submission.contentUrl} 
                          alt="Content"
                          className="w-full max-h-96 object-contain rounded-lg"
                        />
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Additional Notes */}
              {submission?.additionalNotes && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Notes</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-800 whitespace-pre-wrap">{submission.additionalNotes}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* REVIEW SUBMISSION DISPLAY (OLD SYSTEM) */
            <>
              {/* Rating */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Rating</h4>
                {renderStarRating(submission?.rating || 0)}
              </div>
              
              {/* Review Text */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Review</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800">{submission?.reviewText || 'No review text provided'}</p>
                </div>
              </div>
              
              {/* Pros and Cons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Pros */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Plus className="h-4 w-4 text-green-500 mr-1" />
                    Pros
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {submission?.pros && submission.pros.length > 0 ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {submission.pros.map((pro, index) => (
                          <li key={index} className="text-gray-800">{pro}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">No pros provided</p>
                    )}
                  </div>
                </div>
                
                {/* Cons */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Minus className="h-4 w-4 text-red-500 mr-1" />
                    Cons
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {submission?.cons && submission.cons.length > 0 ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {submission.cons.map((con, index) => (
                          <li key={index} className="text-gray-800">{con}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">No cons provided</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Image Gallery */}
          {submission?.imageUrls && submission.imageUrls.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {submission.imageUrls.map((url, index) => (
                  <div key={index} className="bg-gray-100 rounded-lg overflow-hidden h-40">
                    <img
                      src={url}
                      alt={`Review image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f3f4f6" width="300" height="300"/%3E%3Ctext fill="%236b7280" font-family="Arial" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage Error%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Video Gallery */}
          {submission?.videoUrls && submission.videoUrls.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Videos</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {submission.videoUrls.map((url, index) => (
                  <div key={index} className="bg-gray-100 rounded-lg overflow-hidden">
                    <video
                      src={url}
                      className="w-full h-48 object-cover"
                      controls
                      preload="metadata"
                      onError={(e) => {
                        console.error('Error loading video:', e);
                      }}
                    />
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                      Video {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Content Links */}
          {submission?.contentLinks && submission.contentLinks.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Content Links</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="space-y-2">
                  {submission.contentLinks.map((link, index) => (
                    <li key={index} className="flex items-start">
                      <LinkIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-dark underline break-all"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Submission Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Submission Date</h4>
              <p className="text-gray-800">
                {submission?.submittedAt ? new Date(submission.submittedAt.toDate()).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>
          
          {/* Feedback Section */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-4">Review & Feedback</h4>
            
            {/* Feedback Form */}
            {approvalStatus === 'pending' ? (
              <form onSubmit={(e) => e.preventDefault()}>
                <CSRFToken />
                <div className="mb-4">
                  <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback (optional for approval, required for revision request)
                  </label>
                  <textarea
                    id="feedback"
                    rows="4"
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Provide feedback to the influencer..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                  ></textarea>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    disabled={processing}
                    className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    onClick={handleRequestRevision}
                  >
                    {processing ? 'Processing...' : 'Request Revision'}
                  </button>
                  <button
                    type="button"
                    disabled={processing}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    onClick={handleApproveSubmission}
                  >
                    {processing ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start">
                  {approvalStatus === 'approved' ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {approvalStatus === 'approved' ? 'Approved' : 'Rejected'} on{' '}
                      {submission?.updatedAt && submission.updatedAt.toDate
                        ? new Date(submission.updatedAt.toDate()).toLocaleDateString()
                        : submission?.updatedAt instanceof Date
                          ? submission.updatedAt.toLocaleDateString()
                          : submission?.updatedAt
                            ? new Date(submission.updatedAt).toLocaleDateString()
                            : new Date().toLocaleDateString()}
                    </p>
                    <p className="text-gray-700 mt-1">{feedback || 'No feedback provided'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentApprovalDetail;
